/**
 * Servicio de noticias. SOLO LECTURA.
 *
 * Esta web solo enseña noticias: la gestión (alta, edición, publicación,
 * borrado) se hace desde el CMS aparte (Web.CMS.React), que habla con el mismo
 * backend. Por eso aquí no hay ningún `createNews`/`updateNews`/`deleteNews` —
 * no añadir uno sin hablarlo antes, es una decisión de arquitectura.
 *
 * Las dos funciones de este fichero deciden por dentro de dónde salen los
 * datos:
 *
 * - si hay API configurada (`VITE_API_BASE_URL`), llaman por HTTP;
 * - si no, no hay noticias del panel que enseñar (sin API no hay backend del
 *   que leer, y aquí no se guarda nada en memoria).
 *
 * Las noticias «estáticas» son las que viven en `src/data/noticias.ts`, dentro
 * del propio código, y no pasan por este servicio: `noticiasPublicas.ts` las
 * mezcla directamente con lo que devuelve `getPublishedNews`, y `NoticiaPage`
 * las resuelve por su cuenta contra `data/noticias.ts`.
 */

import { isApiEnabled } from './api/endpoints';
import * as noticiasApi from './api/noticiasApi';

export interface News {
  id: string;
  /**
   * Identificador legible para la URL pública (`/noticias/{slug}`).
   *
   * Lo rellena el backend real. Todavía no se usa para enrutar (la ruta sigue
   * siendo `/noticias/:id`); está aquí para cuando se migre.
   */
  slug?: string;
  title: string;
  content: string; // HTML con formato
  /** Resumen para el listado. Lo rellena el API. */
  excerpt?: string;
  /** Autor / firma de la noticia. */
  author?: string;
  /** Enlace externo si la noticia remite a otra web. */
  externalUrl?: string;
  imageData: string; // URL de la imagen servida por el API
  imageFileName: string; // Nombre del archivo, para referencia
  /**
   * Esta web solo lee noticias publicadas: no hay `'pending'`. Ese estado
   * (y `'archived'`) solo tiene sentido en el panel de revisión, que ahora
   * vive en el CMS.
   */
  status: 'published';
  /**
   * Marca la noticia como destacada en el listado público.
   *
   * Solo se ve una: si hay varias marcadas, la página de noticias muestra la
   * más reciente y las demás salen en la rejilla como cualquier otra.
   */
  featured?: boolean;
  createdAt: number;
  publishedAt?: number;
}

/** Todas las publicadas. Para la web pública. Sin API, no hay ninguna. */
export async function getPublishedNews(signal?: AbortSignal): Promise<News[]> {
  if (!isApiEnabled()) return [];
  return noticiasApi.listarNoticiasPublicadas(signal);
}

/**
 * Noticia por id, para la página de detalle.
 *
 * El backend real no tiene GET por id (solo por slug), así que se busca
 * dentro del listado de publicadas ya cargado. Las estáticas del código NO
 * pasan por aquí: `NoticiaPage` las resuelve directamente contra
 * `data/noticias.ts`.
 */
export async function getNewsById(id: string, signal?: AbortSignal): Promise<News | undefined> {
  if (!isApiEnabled()) return undefined;

  const publicadas = await noticiasApi.listarNoticiasPublicadas(signal);
  return publicadas.find((news) => news.id === id);
}
